import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, X, Radio, History } from 'lucide-react';
import { api, fcfa } from '../lib/api';
import { CategoryBadge } from '../lib/categories';

const HKEY = 'ssl-search-history';
const readHistory = () => { try { return JSON.parse(localStorage.getItem(HKEY) || '[]'); } catch { return []; } };

export const SearchBar = ({ category, onLivesChange, compact = false }) => {
  const nav = useNavigate();
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState(readHistory);

  const remember = (term) => {
    const next = [term, ...history.filter((h) => h.toLowerCase() !== term.toLowerCase())].slice(0, 6);
    setHistory(next); localStorage.setItem(HKEY, JSON.stringify(next));
  };
  const clearHistory = () => { setHistory([]); localStorage.removeItem(HKEY); };

  useEffect(() => {
    if (q.trim().length < 2 || compact) { setRes(null); onLivesChange(null); return; }
    const id = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get('/search', { params: { q, ...(category ? { category } : {}) } });
        setRes(data); onLivesChange(data.lives);
        if (data.lives.length || data.products.length) remember(q.trim());
      } catch { setRes(null); onLivesChange(null); }
      finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(id);
  }, [q, category]);

  if (compact) {
    return (
      <form data-testid="search-section" onSubmit={(e) => { e.preventDefault(); if (q.trim()) { remember(q.trim()); nav(`/shop?q=${encodeURIComponent(q.trim())}`); } }} className="relative max-w-3xl mx-auto">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un article, une marque, une boutique…"
          className="w-full pl-11 pr-28 py-3 rounded-full bg-[#0b1120]/80 border border-white/10 text-white placeholder-slate-500 focus:border-[#E8B84A]/70 focus:ring-2 focus:ring-[#E8B84A]/15 outline-none transition-colors" />
        {q && <button type="button" data-testid="search-clear" onClick={() => setQ('')} className="absolute right-24 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>}
        <button type="submit" data-testid="search-submit" className="absolute right-1.5 top-1/2 -translate-y-1/2 px-4 py-2 rounded-full btn-gold text-xs font-bold">Rechercher</button>
      </form>
    );
  }
  return (
    <div className="mb-6" data-testid="search-section">
      <div className="relative max-w-2xl">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
        <input data-testid="search-input" value={q} onChange={(e) => setQ(e.target.value)}
          placeholder="Rechercher une marque, une taille, une longueur, un article… (ex : Nike 42, perruque 22&quot;, Levi's)"
          className="w-full pl-11 pr-11 py-3 rounded-2xl bg-slate-900/70 border border-slate-800 text-white placeholder-slate-500 focus:border-sky-400 outline-none" />
        {q && <button data-testid="search-clear" onClick={() => setQ('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-white"><X className="h-4 w-4" /></button>}
      </div>
      {history.length > 0 && !q && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs" data-testid="search-history">
          <span className="inline-flex items-center gap-1 text-slate-500"><History className="h-3.5 w-3.5" />Récentes :</span>
          {history.map((h) => (
            <button key={h} data-testid={`history-${h}`} onClick={() => setQ(h)}
              className="px-2.5 py-1 rounded-full border border-slate-700 text-slate-300 hover:border-sky-400/60 hover:text-white transition-colors">{h}</button>
          ))}
          <button data-testid="history-clear" onClick={clearHistory} className="text-slate-500 hover:text-white underline-offset-2 hover:underline">Effacer</button>
        </div>
      )}
      {res && (
        <div className="mt-4" data-testid="search-results">
          <div className="text-sm text-slate-400 mb-2">
            {busy ? 'Recherche…' : `${res.lives.length} live(s) · ${res.products.length} article(s) pour « ${q} »`}
          </div>
          {res.products.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
              {res.products.map((p) => (
                <Link key={p.product_id} to={p.live_id ? `/live/${p.live_id}` : `/seller/${p.seller_id}`} data-testid={`search-prod-${p.product_id}`}
                  className="group rounded-xl bg-slate-900/60 border border-slate-800 hover:border-rose-500/50 overflow-hidden transition">
                  <div className="relative h-28">
                    {p.image_url && <img src={p.image_url} alt="" className="h-full w-full object-cover group-hover:scale-105 transition" />}
                    {p.live_id && <span className="absolute top-2 left-2 text-[10px] font-bold px-2 py-0.5 rounded-full bg-rose-600 text-white inline-flex items-center gap-1"><Radio className="h-3 w-3" />EN LIVE</span>}
                  </div>
                  <div className="p-2.5">
                    <div className="text-sm font-bold truncate">{p.name}</div>
                    <CategoryBadge category={p.category} size={p.size} brand={p.brand} className="mt-1" />
                    <div className="mt-1 text-amber-400 font-mono-num text-xs">{fcfa(p.starting_price)}</div>
                    <div className="text-[10px] text-slate-500 truncate">{p.seller_name}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
